/* ───────────────────────────────────────────────────────────────────────────
   O16 Extended Delivery Consent — the ONE shared gate every add-to-cart
   code path in the theme calls before adding an item whose delivery date is
   extended (pre-order / back-order, resolved server-side by
   snippets/o16-preback-status.liquid). Consent is captured once per browser
   and remembered forever (no customer accounts in this store, so
   localStorage is the only durable store) — but every single extended add
   ALSO stamps a cart attribute, independent of whether the modal was shown
   this time, so the order itself always carries proof.

   ── THE THREE CALLERS (two code paths) ────────────────────────────────────
   1. assets/product-form.js — the PDP add-to-cart form. The sticky buy bar
      (assets/o16-sticky-buybar.js) docks this exact <product-form> element
      in place rather than cloning it, so it is covered automatically.
      Desktop quick-view re-uses the same <product-form> markup fetched via
      the Section Rendering API, so it is covered automatically too.
   2. assets/o16-cross-sell.js — "Pairs well with" cross-sell cards inside
      the cart drawer, direct POST /cart/add.js.
   3. assets/o16-cart-drawer.js (O16CookbookAdd) — the cookbook free-shipping
      upsell inside the cart drawer, direct POST /cart/add.js.
   All three read the SAME data-o16-extended-date / data-o16-extended-type
   pair, baked server-side wherever a real add can start (see each file's
   own comments for exactly which element carries them), and all three call
   window.O16ExtendedDelivery.guard(el) before the actual /cart/add.js call.

   ── MEMORY: ONE YES, NEVER AGAIN ──────────────────────────────────────────
   Storage key O16_STORAGE_KEY holds the ISO timestamp the customer first
   said yes. Its presence alone means "skip the modal, but still stamp" —
   the value itself is only ever used for the cart attribute's timestamp on
   the FIRST consent; later merges keep the ORIGINAL granted timestamp (see
   mergeAndStampAttribute) so the order always shows when consent was
   genuinely given, not just when this particular item was added.

   ── WHY THE CHECKBOX SENTENCE IS GENERIC ──────────────────────────────────
   Because one yes must cover every future extended-delivery product too —
   see snippets/o16-extended-delivery-modal.liquid's own header comment. The
   concrete date/type are shown as CONTEXT above the checkbox, never inside
   the sentence itself.

   ── THE CART ATTRIBUTE ────────────────────────────────────────────────────
   Name: "Extended delivery payment consent" — reads clearly on the order in
   Shopify admin. Value: "Yes, consent given <ISO timestamp>". Set via
   /cart/update.js, which REPLACES the whole attributes object — so this
   always reads the current cart's attributes first and merges in the one
   key, never clobbering anything else (an app, another feature) may have
   set.
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var O16_STORAGE_KEY = 'o16_extended_delivery_consent';
  var O16_ATTRIBUTE_NAME = 'Extended delivery payment consent';

  // EXPECTED-DELIVERY STAMP (owner, 2026-09-08). The order confirmation
  // email carries a sentence about when the order will arrive, conditional
  // on this attribute. Without it that sentence can never render, so the
  // customer is told the order ships later but never when.
  //
  // Two keys, on purpose:
  //  - the visible one holds a ready-made sentence fragment ("in 5 weeks" /
  //    "on Wed 07 Oct") so the email never has to do date maths, and so the
  //    weeks-not-dates rule for far-off items is decided HERE, once, by the
  //    same threshold the rest of the feature uses.
  //  - the underscore-prefixed one holds the raw ISO date. Shopify hides
  //    underscore-prefixed attributes from the customer, and ISO dates sort
  //    lexicographically, so "is this new item later than what we already
  //    promised" is a plain string comparison with no parsing.
  // The whole order ships together once every item is ready, so the LATEST
  // date across the cart is the one that is true. Stamping the last item
  // added would let a five-week item silently overwrite an eleven-week one
  // and turn the email into a promise the warehouse cannot keep.
  var O16_EXPECTED_NAME = 'Expected delivery';
  var O16_EXPECTED_RAW_NAME = '_Expected delivery date';

  // TEN-DAY CONSENT THRESHOLD (owner decision, 2026-09-08) — the JS twin of
  // O16_EXT_GATE_THRESHOLD_DAYS in snippets/o16-extended-delivery-gate.liquid
  // (search that file for the same name). The two MUST always agree — this
  // is needed here (not just server side) because a variant switch has no
  // server round trip to recompute which side of the threshold the newly
  // selected variant's own date falls on. Below this many days out, the
  // item's date is treated as concrete enough that a consent modal is out
  // of proportion (the modal exists because payment happens long before
  // shipment; at ten days that gap is no longer meaningful) — see the
  // Liquid snippet's own header comment for the full rationale, the
  // inclusive-boundary rule ("10 or fewer" = date side), and why stale
  // dates need no separate handling here (they never reach this far —
  // snippets/o16-delivery-ladder-resolve.liquid already excludes them
  // before a date ever gets this far downstream).
  var O16_EXT_GATE_THRESHOLD_DAYS = 20;

  // TEST HANDLE — ?o16-reset-consent on any URL forgets the stored yes, so the
  // modal appears again on the very next add. It can only ever FORGET consent,
  // never grant it, so the worst a stranger can do with it is see the modal one
  // more time. That asymmetry is why this is safe to ship: a private window is
  // the alternative, and a private window also drops the theme preview cookie,
  // which sends the tester to the live theme and hides the whole feature.
  try {
    if (window.location.search.indexOf('o16-reset-consent') !== -1) {
      window.localStorage.removeItem('o16_extended_delivery_consent');
    }
  } catch (e) {
    // Storage can throw outright in locked down browsers. Nothing to reset then.
  }

  function getRemembered() {
    try {
      return window.localStorage.getItem(O16_STORAGE_KEY) || null;
    } catch (e) {
      // Private-browsing / storage-disabled — treat as "never asked", the
      // modal will simply show every time in that browser (fail toward
      // asking again, never fail toward silently skipping consent).
      return null;
    }
  }

  function setRemembered(iso) {
    try {
      window.localStorage.setItem(O16_STORAGE_KEY, iso);
    } catch (e) {
      /* Same fallback as above — nothing else to do if storage is blocked. */
    }
  }

  // Reads the extended-delivery signal off the element that carries it.
  // Every caller passes the SAME element its markup baked the two data
  // attributes onto (see each call site) — no closest()/traversal here, so
  // there is never any ambiguity about which item is being gated.
  function readExtendedInfo(el) {
    if (!el || !el.dataset) return { extended: false };
    var date = (el.dataset.o16ExtendedDate || '').trim();
    var type = (el.dataset.o16ExtendedType || '').trim();
    if (!date || !type) return { extended: false };
    // raw is carried purely so the order stamp can compare two extended
    // items and keep the LATER one (see mergeAndStampAttribute). It is
    // never shown; the modal keeps using `date`/`type` exactly as before.
    var raw = (el.dataset.o16ExtendedDateRaw || '').trim();
    return { extended: true, date: date, type: type, raw: raw };
  }

  // SHIPPING DATE (owner, 2026-09-16) supersedes the note below; customers now
  // always see the estimated shipping date. Was: DURATION, NOT A DATE (owner decision, 2026-09-07): an exact calendar
  // date on an item not yet in the warehouse is a promise the store does
  // not control, so every customer-facing extended-delivery date becomes
  // "N weeks" (bare, no "Approx." prefix — callers add their own casing).
  // Same formula, same rounding as the Liquid source of truth
  // (snippets/o16-weeks-until.liquid): days from today to the target date,
  // divided by 7, ROUNDED UP, both dates normalised to midnight so the
  // result is stable across the whole day. Needed here (not just server
  // side) because a variant switch has no server round trip to recompute
  // it — see syncExtendedFlagForVariant below.
  // ESTIMATED SHIPPING (owner, 2026-09-16): customers now always see a date
  // (formatExtendedDate), never a week count. Shared day-math for isUnderGateThreshold
  // (the 10-day threshold) — one normalisation (midnight-to-midnight,
  // whole days), one place, so the two can never quietly disagree about
  // what "N days away" means for the same raw date. Returns null for a
  // blank/unparseable date (callers each decide their own safe default).
  function daysUntil(isoDateStr) {
    if (!isoDateStr) return null;
    var parts = isoDateStr.split('-');
    if (parts.length !== 3) return null;
    var target = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(target.getTime())) return null;
    target.setHours(0, 0, 0, 0);
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / 86400000);
  }

  // GALLERY PILL WEEKS (owner, 2026-10-06): the gallery pill alone shows
  // "PRE ORDER: 5 WEEKS" again; ceil(days/7), minimum 1 — same formula as
  // snippets/o16-weeks-until.liquid. Keep the two in sync.
  function weeksUntilLabel(isoDateStr) {
    var days = daysUntil(isoDateStr);
    if (days === null) return '';
    var weeks = Math.ceil(days / 7);
    if (weeks < 1) weeks = 1;
    return weeks === 1 ? '1 week' : weeks + ' weeks';
  }


  // TEN-DAY CONSENT THRESHOLD (owner, 2026-09-08) — see the
  // O16_EXT_GATE_THRESHOLD_DAYS declaration above + snippets/o16-extended-
  // delivery-gate.liquid for the full rule. INCLUSIVE: "10 or fewer" days
  // resolves true (within the threshold) — the boundary itself belongs to
  // the date side, mirroring the Liquid snippet's own `<=`. A blank/
  // unparseable date fails toward false (treat as OVER the threshold, i.e.
  // gate REQUIRED) — same fail-safe direction as the Liquid twin: never
  // silently drop a consent requirement because a date could not be read.
  // MASTER SWITCH (2026-10-30): theme setting o16_consent_popup_enabled, passed
  // as window.O16_CONSENT_POPUP_ENABLED by snippets/o16-extended-delivery-modal.
  // When not exactly true, nothing is ever over the threshold: dates are shown
  // and no consent flow runs (mirrors the Liquid gate snippet).
  function isUnderGateThreshold(isoDateStr) {
    if (window.O16_CONSENT_POPUP_ENABLED !== true) return true;
    var days = daysUntil(isoDateStr);
    if (days === null) return false;
    return days <= O16_EXT_GATE_THRESHOLD_DAYS;
  }

  var O16_WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var O16_MONTH_ABBR = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // WITHIN-THRESHOLD DISPLAY (owner, 2026-09-08): a concrete calendar date,
  // in the SAME "%a %d %b" shape Liquid's `date` filter already renders
  // everywhere else in this feature (snippets/o16-preback-status.liquid's
  // own date_fmt field, etc.) — e.g. "Wed 17 Sep". Built from fixed English
  // abbreviation tables rather than toLocaleDateString: this store is
  // English-only and a locale-dependent formatter would risk silently
  // drifting from the server-rendered string on a browser set to a
  // different locale. Callers apply their own casing (the label wants it
  // upcased, the modal never shows it at all — see the file header).
  function formatExtendedDate(isoDateStr) {
    if (!isoDateStr) return '';
    var parts = isoDateStr.split('-');
    if (parts.length !== 3) return '';
    var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(d.getTime())) return '';
    // "26 Oct" (owner, 2026-09-16) — same shape as the Liquid side's
    // date: '%-d %b' in o16-preback-status / o16-preback-cart-status.
    // \u00a0 (non-breaking space) keeps "27 Nov" on one line everywhere;
    // the Liquid side does the same with replace: ' ', '<U+00A0>'.
    return String(d.getDate()) + '\u00a0' + O16_MONTH_ABBR[d.getMonth()];
  }

  function cartRoutes() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return { cartJson: base + '.js', update: base + '/update.js' };
  }

  // Merge-safe attribute stamp: GET the live cart, spread its existing
  // attributes, overwrite/add ONLY our own key, POST the merged set. Runs
  // for EVERY extended add — remembered or freshly confirmed — so the order
  // always carries proof even on a visit where the modal never appeared.
  // Builds the customer-facing fragment for one raw date, using the SAME
  // ten-day threshold as everything else: far off reads as a duration, near
  // reads as a day. A fragment rather than a bare value so the email can
  // drop it straight into a sentence without knowing which shape it got.
  function expectedFragment(rawISO) {
    // ESTIMATED SHIPPING (owner, 2026-09-16): always the shipping day,
    // "on 26 Oct". The key names stay "Expected delivery" /
    // "_Expected delivery" because the order confirmation note
    // (docs/specs/ordrebekraeftelse-note.liquid) reads them by name.
    if (!rawISO) return '';
    var day = formatExtendedDate(rawISO);
    return day ? 'on ' + day : '';
  }

  function mergeAndStampAttribute(grantedISO, info) {
    var routes = cartRoutes();
    return fetch(routes.cartJson, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var attributes = {};
        var existing = (cart && cart.attributes) || {};
        Object.keys(existing).forEach(function (key) {
          attributes[key] = existing[key];
        });
        attributes[O16_ATTRIBUTE_NAME] = 'Yes, consent given ' + grantedISO;

        // LATEST DATE WINS. ISO dates compare correctly as plain strings, so
        // this keeps whichever of the two is further out and leaves the
        // stamp untouched when the item being added arrives sooner than
        // something already in the cart.
        var raw = (info && info.raw) || '';
        if (raw) {
          var promised = existing[O16_EXPECTED_RAW_NAME] || '';
          if (!promised || raw > promised) {
            var fragment = expectedFragment(raw);
            if (fragment) {
              attributes[O16_EXPECTED_RAW_NAME] = raw;
              attributes[O16_EXPECTED_NAME] = fragment;
            }
          }
        }

        return fetch(routes.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        });
      })
      .then(function (res) {
        return res.json();
      })
      .catch(function (err) {
        // A stamping failure must never block the add itself — genuine,
        // confirmed consent has already been captured by this point (either
        // just now via the modal, or previously via localStorage). Only the
        // order-level PROOF is missing for this one add; logged so it is
        // never silently swallowed.
        // eslint-disable-next-line no-console
        console.error('O16 extended delivery: could not stamp consent attribute.', err);
      });
  }

  function getModal() {
    return document.querySelector('o16-extended-modal');
  }

  // Opens the shared modal populated with this item's own date/type and
  // returns a Promise<boolean> — true on Confirm, false on Cancel/close/
  // overlay/Escape. Fails CLOSED (resolves false, nothing added) if the
  // modal element somehow isn't on the page rather than silently letting an
  // unconsented extended-delivery item through.
  function requestConsent(info) {
    var modal = getModal();
    if (!modal || typeof modal.request !== 'function') {
      return Promise.resolve(false);
    }
    return modal.request(info);
  }

  // The one shared gate. Returns a Promise<boolean> — true means "safe to
  // proceed with the add", false means "the customer cancelled, do not add
  // anything". Resolves true immediately (no modal, no stamp, no
  // localStorage write) for any item that isn't extended-delivery at all.
  function guard(el) {
    if (window.O16_CONSENT_POPUP_ENABLED !== true) return Promise.resolve(true);
    var info = readExtendedInfo(el);
    if (!info.extended) return Promise.resolve(true);

    var remembered = getRemembered();
    if (remembered) {
      return mergeAndStampAttribute(remembered, info).then(function () {
        return true;
      });
    }

    return requestConsent(info).then(function (confirmed) {
      if (!confirmed) return false;
      var grantedISO = new Date().toISOString();
      setRemembered(grantedISO);
      syncConsentProperty();
      return mergeAndStampAttribute(grantedISO, info).then(function () {
        return true;
      });
    });
  }

  // formatExtendedDate is exposed here (not just used internally) because
  // assets/o16-preback-sync.js needs the EXACT same conversion for its own
  // variant-switch labels (cart-line "Estimated shipping 26 Oct" +
  // the gallery back/pre-order pill) — one formula, one owner, reused by
  // reference, never re-implemented a second time where it could drift
  // from snippets/o16-weeks-until.liquid. Safe to depend on: this script is
  // rendered globally from layout/theme.liquid (unconditionally, every
  // page), so it always has run and set this global before a shopper can
  // actually trigger a variant switch on the PDP.
  //
  // hasConsent / rememberConsent / attributeName / cartRoutes are exposed
  // for assets/o16-checkout-shield.js (owner escalation, 2026-09-08) — the
  // express-checkout wallet buttons on the PDP and in the cart drawer sit
  // behind a closed shadow DOM this file's own guard()/modal already solved
  // for the add-to-cart path. The shield reuses THESE exact primitives
  // (same localStorage key, same cart-attribute name, same /cart routes)
  // rather than re-implementing any of them, so there is only ever ONE
  // consent store in the whole theme.
  window.O16ExtendedDelivery = {
    guard: guard,
    weeksUntilLabel: weeksUntilLabel,
    // TEN-DAY THRESHOLD (owner, 2026-09-08) — exposed so
    // assets/o16-preback-sync.js can apply the SAME gate decision on a
    // variant switch (gallery label + reassurance block) without a second,
    // possibly-drifting copy of the day-math or the constant itself. See
    // O16_EXT_GATE_THRESHOLD_DAYS above.
    isUnderGateThreshold: isUnderGateThreshold,
    formatExtendedDate: formatExtendedDate,
    gateThresholdDays: O16_EXT_GATE_THRESHOLD_DAYS,
    hasConsent: function () {
      return !!getRemembered();
    },
    rememberConsent: function (iso) {
      setRemembered(iso);
      // Both consent paths funnel through here — guard() below and the
      // wallet shield in assets/o16-checkout-shield.js — so refreshing the
      // line item property here is what keeps the two from drifting apart
      // again. #7459 was lost precisely because the shield path recorded
      // nothing the order could carry.
      syncConsentProperty();
    },
    syncConsentProperty: syncConsentProperty,
    // Exposed for assets/o16-bundle-ladder.js (owner 2026-09-09). The bundle
    // ladder lets a shopper buy a DIFFERENT product's variant from this page
    // by rewriting the form's id, which no `variant:change` ever announces —
    // so it has to move the gate itself. It calls THIS function rather than
    // reading the map again, so there stays exactly one place that decides
    // what "gated" means and one place that applies the ten-day threshold.
    syncExtendedFlagForVariant: syncExtendedFlagForVariant,
    attributeName: O16_ATTRIBUTE_NAME,
    cartRoutes: cartRoutes
  };

  /* ── PDP VARIANT REACTIVITY ────────────────────────────────────────────
     PER-VARIANT MAP (owner escalation, 2026-09-07, PADELLA case: Ø24 in
     stock, Ø28 back-order 29 Oct, Ø32/Ø36 back-order 07 Oct — three states,
     two dates, one product). The old version of this function assumed the
     PAGE-LOAD variant's date/type applied to every "covered" variant — true
     for the manual 5-slot system (type/date ARE product-level there), false
     for the delivery-ladder system, where each variant carries its OWN
     date/type. Silently showing/gating the WRONG variant's date, or gating
     nothing at all for a variant o16-preback-status never got a chance to
     resolve, defeats the whole point of this feature, so this now parses
     the FULL per-variant table baked onto data-o16-extended-variant-map by
     snippets/main-product-section.liquid (via
     snippets/o16-extended-delivery-map.liquid) and looks up the newly
     selected variant's own entry directly — no assumption that any two
     variants share a date.

     Format: "id:TYPE:DATE;id:TYPE:DATE;..." (semicolon-separated entries,
     first two colons per entry are the field separators — mirrors
     o16-preback-sync.js's own lookupRestockDate() map format for Feature
     2's re-stock dates, the same "no server round trip on variant switch"
     idea, not a new encoding). TYPE is humanized here (client-side) for
     this feature's own customer-facing modal copy — see readExtendedInfo/
     requestConsent above — since the reassurance line and gallery label
     (assets/o16-preback-sync.js) want the raw upcased form instead and
     read the SAME underlying map value verbatim for their own purposes.

     The PDP section renders TWICE (desktop + mobile wrappers, duplicate
     ids — a known, established condition of this theme), so this always
     targets every <product-form> on the page via querySelectorAll, never
     getElementById/querySelector. */
  function parseVariantMap(mapAttr) {
    var map = {};
    if (!mapAttr) return map;
    mapAttr.split(';').forEach(function (entry) {
      if (!entry) return;
      var sep1 = entry.indexOf(':');
      if (sep1 === -1) return;
      var id = entry.slice(0, sep1);
      var rest = entry.slice(sep1 + 1);
      var sep2 = rest.indexOf(':');
      if (sep2 === -1) return;
      var type = rest.slice(0, sep2);
      var date = rest.slice(sep2 + 1);
      if (id && type && date) map[id] = { type: type, date: date };
    });
    return map;
  }

  // "BACK ORDER" / "PRE-ORDER" (the raw upcased label) -> "back order" for
  // this feature's own customer-facing copy (modal context line + checkbox
  // sentence context).
  //
  // Deliberately all lower case: the label only ever appears MID sentence
  // ("This item is a back order."), where a capital reads like a proper noun
  // and makes the sentence look shouty. The upper case form still belongs on
  // the gallery pill, which is a label rather than prose, and that one is
  // upper cased by CSS rather than here.
  function humanizeType(type) {
    if (!type) return '';
    return type.replace(/-/g, ' ').toLowerCase();
  }

  function syncExtendedFlagForVariant(variantId) {
    document.querySelectorAll('product-form[data-o16-extended-variant-map]').forEach(function (form) {
      var map = parseVariantMap(form.getAttribute('data-o16-extended-variant-map'));
      var entry = variantId != null ? map[String(variantId)] : null;
      // TEN-DAY THRESHOLD (owner, 2026-09-08): a variant whose OWN date
      // falls within the threshold is not gated at all — deleting both
      // data attributes here (the same branch a non-matched variant
      // already takes) is what makes guard()/readExtendedInfo() and the
      // CSS-driven PDP wallet shield (assets/o16-extended-delivery-
      // modal.css, `product-form[data-o16-extended-date]`) treat it as a
      // perfectly ordinary add — no new "skip" branch needed, presence of
      // the attribute IS the gate signal everywhere downstream already
      // reads it. entry.date is the RAW ISO date carried by the variant
      // map (see the file header + snippets/o16-extended-delivery-map.
      // liquid); this attribute is what readExtendedInfo()/the consent
      // modal's context line actually display, so when the gate IS
      // required it must already be the customer-facing weeks phrase,
      // exactly like the SSR value it replaces on variant switch
      // (main-product-section.liquid's data-o16-extended-date =
      // o16_ext_weeks_phrase at page load, only ever rendered there when
      // that same threshold already resolved "over").
      if (entry && !isUnderGateThreshold(entry.date)) {
        form.dataset.o16ExtendedDate = formatExtendedDate(entry.date);
        form.dataset.o16ExtendedType = humanizeType(entry.type);
        // The RAW date has to move with the other two. Both the cart stamp
        // and the line item record read it, and on a product whose
        // variants carry DIFFERENT dates (the PADELLA case this whole
        // per-variant map exists for) a stale raw date would promise the
        // previously selected variant's arrival for the one actually
        // being bought.
        form.dataset.o16ExtendedDateRaw = entry.date;
      } else {
        delete form.dataset.o16ExtendedDate;
        delete form.dataset.o16ExtendedType;
        delete form.dataset.o16ExtendedDateRaw;
      }
    });
  }

  /* ── THE CONSENT RECORD ON THE LINE ITEM ───────────────────────────────
     The cart attribute is not enough on its own. The PDP express buttons
     (Shop Pay / Google Pay / Apple Pay) buy the selected variant straight
     from the product form and never touch the cart, so a cart attribute
     cannot reach that order at all. Proven on live order #7459: a Google
     Pay purchase of a back-order item arrived with no attributes
     whatsoever, while the ordinary card checkout on #7457 carried the
     consent stamp perfectly.

     A hidden line item property rides on the form itself, so it survives
     every path that submits it — ordinary add to cart AND express
     checkout. Underscore-prefixed, so Shopify keeps it out of the
     customer's view while it stays visible on the order in admin, which
     is where the record is actually needed.

     The value is the REMEMBERED consent timestamp, not the current time,
     so it stays byte-identical across renders. A value that changed on
     every page load would make Shopify treat each add as a different line
     and split one item into several. */
  var O16_LINE_PROPERTY_NAME = 'properties[_Extended delivery consent]';
  // The date has to travel with the consent for the same reason: on the
  // express path there is no cart, so the order confirmation has no cart
  // attribute to read and would fall back to "we will write to you when we
  // know", on an order where we DO know.
  var O16_LINE_EXPECTED_NAME = 'properties[_Expected delivery]';

  function setHiddenProperty(form, name, value) {
    var el = form.querySelector('input[name="' + name + '"]');
    if (!value) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    if (el) {
      if (el.value !== value) el.value = value;
      return;
    }
    el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    el.value = value;
    (form.querySelector('form') || form).appendChild(el);
  }

  function syncConsentProperty() {
    var granted = getRemembered();
    document.querySelectorAll('product-form').forEach(function (form) {
      // The gate signal and the record must agree: the property belongs on
      // a form only while that form's CURRENT variant is actually gated,
      // which is exactly what the date attribute means everywhere else in
      // this file. A variant switch to an in-stock option therefore
      // removes it again.
      var gated = !!(form.dataset && form.dataset.o16ExtendedDate);
      if (!gated || !granted) {
        setHiddenProperty(form, O16_LINE_PROPERTY_NAME, '');
        setHiddenProperty(form, O16_LINE_EXPECTED_NAME, '');
        return;
      }
      setHiddenProperty(form, O16_LINE_PROPERTY_NAME, 'Yes, consent given ' + granted);
      setHiddenProperty(
        form,
        O16_LINE_EXPECTED_NAME,
        expectedFragment((form.dataset.o16ExtendedDateRaw || '').trim())
      );
    });
  }

  // This theme dispatches `variant:change` as a plain DOM CustomEvent on
  // `document` (assets/global.js, VariantSelects.onVariantChange) with
  // event.detail.variant — NOT Dawn's PUB_SUB_EVENTS.variantChange (no
  // publisher anywhere in this theme; same fact o16-preback-sync.js's own
  // header comment documents).
  document.addEventListener('variant:change', function (event) {
    var variant = event && event.detail && event.detail.variant;
    syncExtendedFlagForVariant(variant ? variant.id : null);
    // The gate signal just moved, so the record must follow it.
    syncConsentProperty();
  });

  /* ── THE MODAL CUSTOM ELEMENT ─────────────────────────────────────────── */

  function defineModal() {
    if (customElements.get('o16-extended-modal')) return;

    customElements.define(
      'o16-extended-modal',
      class extends HTMLElement {
        connectedCallback() {
          this._panel = this.querySelector('.o16-extended-modal__panel');
          this._confirmBtn = this.querySelector('[data-o16-extended-confirm]');
          this._dateEl = this.querySelector('[data-o16-extended-date-text]');
          this._typeEl = this.querySelector('[data-o16-extended-type-text]');
          this._resolver = null;
          this._returnFocus = null;
          this._onConfirmSync = null;
          // Captured once, verbatim from the markup, so request()'s
          // optional info.confirmLabel (assets/o16-checkout-shield.js only
          // — "Confirm and add to cart" is simply wrong when nothing is
          // being added to a cart) can always restore the ORIGINAL wording
          // for every other caller without hardcoding that string a second
          // time here.
          this._defaultConfirmText = this._confirmBtn ? this._confirmBtn.textContent : '';

          this._onClick = this._onClick.bind(this);
          this._onKeyup = this._onKeyup.bind(this);
          this.addEventListener('click', this._onClick);
          this.addEventListener('keyup', this._onKeyup);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          this.removeEventListener('keyup', this._onKeyup);
          // A hidden pending promise must never hang forever if this element
          // is ever removed/replaced mid-flow — resolve false (fail closed:
          // nothing gets added without a confirmed yes).
          this._settle(false);
        }

        _onClick(event) {
          if (event.target.closest('[data-o16-extended-close]')) {
            event.preventDefault();
            this._settle(false);
            return;
          }
          if (event.target.closest('[data-o16-extended-confirm]')) {
            event.preventDefault();
            if (this._confirmBtn && this._confirmBtn.disabled) return;
            // Runs SYNCHRONOUSLY, inside THIS trusted click, before the
            // Promise below even resolves — assets/o16-checkout-shield.js
            // relies on exactly that timing to re-trigger a wallet button
            // while this click's user activation is still alive (a
            // Promise .then() callback would still run in the same task,
            // but there is no reason to rely on that subtlety when a plain
            // synchronous callback removes all doubt). See that file's own
            // header comment for the full chain this protects.
            if (typeof this._onConfirmSync === 'function') {
              try {
                this._onConfirmSync();
              } catch (e) {
                // A caller's own bug must never prevent Confirm from
                // resolving the modal.
                // eslint-disable-next-line no-console
                console.error('O16 extended delivery: onConfirmSync threw.', e);
              }
            }
            this._settle(true);
          }
        }

        // Escape closes the MODAL only — stopPropagation before the keyup
        // bubbles to <cart-drawer>, whose own keyup Escape handler would
        // otherwise close the whole drawer underneath it (same pattern as
        // <o16-discount-modal>._onKeyup in o16-cart-drawer.js).
        _onKeyup(event) {
          if ((event.key === 'Escape' || event.key === 'Esc') && !this.hidden) {
            event.stopPropagation();
            this._settle(false);
          }
        }

        // Opens the modal populated with { date, type } and returns a
        // Promise<boolean>. Safe to call again while already open (a second
        // extended item added before this one resolves) — the checkbox/
        // context simply re-populate for the new item and any earlier
        // still-pending caller resolves false via _settle when this new
        // request supersedes it, rather than leaving two callers racing for
        // the same single resolver slot.
        // onConfirmSync (optional) — a plain function called SYNCHRONOUSLY
        // from inside the Confirm button's own click handler, before this
        // Promise resolves. Only assets/o16-checkout-shield.js passes one
        // (see its header comment for why); every other caller in the
        // theme (product-form.js, o16-cross-sell.js, the cookbook upsell)
        // omits it and behaves exactly as before.
        request(info, onConfirmSync) {
          var self = this;
          if (this._resolver) this._settle(false);
          this._onConfirmSync = typeof onConfirmSync === 'function' ? onConfirmSync : null;

          return new Promise(function (resolve) {
            self._resolver = resolve;

            if (self._dateEl) self._dateEl.textContent = info.date || '';
            if (self._typeEl) self._typeEl.textContent = info.type || '';
            if (self._confirmBtn) {
              self._confirmBtn.textContent = info.confirmLabel || self._defaultConfirmText;
            }

            self._returnFocus = document.activeElement;
            self.hidden = false;
            // Third-party floating widgets (Klaviyo popup/teaser, Cookiebot
            // renew widget) are fixed-position and inject on document.body,
            // independent of this modal's own z-index — the SAME issue this
            // theme already solved for the burger menu (see the dedicated
            // body-hook rule in assets/o16-header-split.css). Mirrors that
            // pattern exactly: our OWN class (never the burger's
            // overflow-hidden-tablet/-mobile, so the two features can never
            // clobber each other's state), added on open, removed in
            // _settle() below on every close path (Confirm/Cancel/Escape/
            // overlay all funnel through _settle). Hidden via CSS
            // (assets/o16-extended-delivery-modal.css), never by removing
            // the third party's own DOM nodes.
            document.body.classList.add('o16-extended-modal-open');

            var focusTarget = self._panel;
            if (typeof trapFocus === 'function' && self._panel) {
              trapFocus(self._panel, focusTarget);
            } else if (focusTarget && typeof focusTarget.focus === 'function') {
              focusTarget.focus();
            }
          });
        }

        _settle(confirmed) {
          if (this.hidden) return;
          this.hidden = true;
          // Restore the third-party widgets hidden on open — every close
          // path (Confirm, Cancel, Escape, overlay click) calls _settle().
          document.body.classList.remove('o16-extended-modal-open');

          var resolver = this._resolver;
          this._resolver = null;
          this._onConfirmSync = null;
          var focusEl = this._returnFocus;
          this._returnFocus = null;

          if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(focusEl || undefined);
          } else if (focusEl && typeof focusEl.focus === 'function') {
            focusEl.focus();
          }

          if (resolver) resolver(confirmed);
        }
      }
    );
  }

  defineModal();

  // A browser that already remembers a yes must carry the record from the
  // very first paint — that shopper never sees the modal again, so nothing
  // else would ever put it there before an express tap.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncConsentProperty);
  } else {
    syncConsentProperty();
  }
})();
